import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, FolderOpen, Trash2, Clock } from 'lucide-react'
import { useRewriteStore } from '@/stores/rewriteStore'
import { useAuthStore } from '@/stores/authStore'

export default function RewriteProjectsPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { projects, isLoading, fetchProjects, deleteProject } = useRewriteStore()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    fetchProjects()
  }, [isAuthenticated])

  const handleDelete = async (id: string) => {
    if (!confirm('프로젝트를 삭제하시겠습니까? 관련 콘텐츠도 모두 삭제됩니다.')) return
    setDeletingId(id)
    await deleteProject(id)
    setDeletingId(null)
  }

  return (
    <div className="min-h-[calc(100vh-56px)] p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">리라이트 프로젝트</h1>
          <p className="text-sm text-text-tertiary mt-1">콘텐츠 리라이팅 프로젝트 관리</p>
        </div>
        <button
          onClick={() => navigate('/rewrite/create')}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> 새 프로젝트
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-text-tertiary">
          <FolderOpen className="w-16 h-16 mb-4 opacity-30" />
          <p className="text-lg mb-2">프로젝트가 없습니다</p>
          <p className="text-sm mb-6">새 프로젝트를 만들어 콘텐츠를 생성해보세요</p>
          <button onClick={() => navigate('/rewrite/create')} className="btn btn-primary">
            새 프로젝트 만들기
          </button>
        </div>
      ) : (
        <div className="grid gap-3">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-bg-secondary border border-border-subtle rounded-xl p-4 hover:border-primary/30 transition-colors cursor-pointer flex items-center justify-between"
              onClick={() => navigate(`/rewrite/create/${project.id}`)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold truncate">{project.title}</h3>
                  <span className="text-xs px-2 py-0.5 rounded bg-bg-elevated text-text-tertiary">
                    {project.status}
                  </span>
                </div>
                {project.description && (
                  <p className="text-sm text-text-secondary mt-1 truncate">{project.description}</p>
                )}
                <div className="flex items-center gap-3 mt-2 text-xs text-text-tertiary">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(project.updated_at).toLocaleDateString('ko-KR')}
                  </span>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(project.id) }}
                disabled={deletingId === project.id}
                className="p-2 text-text-tertiary hover:text-red-400 rounded-lg hover:bg-bg-elevated transition-colors ml-2"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
